import { useEffect, useRef, useState, useCallback } from 'react';
import Hls from 'hls.js';
import { usePlayer } from '../../stores/PlayerContext';
import { RTHK_LIVE_STREAMS, getLiveStreamFallbackUrls } from '../../services/rthk';
import { getFallbackUrls, fetchCurrentLiveProgram } from '../../services/rthkApi';

interface Episode {
  id: string;
  programId: string;
  channelId: string;
  title: string;
  description?: string;
  publishDate?: string;
  duration?: number;
  audioUrl?: string;
  startTime?: number;
  programTitle?: string;
}

export default function AudioPlayer() {
  const { currentChannel, currentEpisode } = usePlayer() as {
    currentChannel: {
      id: string;
      name: string;
      description?: string;
      streamUrl?: string;
    } | null;
    currentEpisode: Episode | null;
  };

  // =========================================================
  // Audio / HLS refs
  // =========================================================

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const isPlayingRef = useRef(false);
  const isSeekingRef = useRef(false);
  const pollingTimerRef = useRef<number | null>(null);
  const fallbackIndexRef = useRef(0);

  // =========================================================
  // UI state
  // =========================================================

  const [isExpanded, setIsExpanded] = useState(true);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const [liveProgramInfo, setLiveProgramInfo] = useState<{
    program: string;
    host: string;
    time: string;
  } | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const lastEpisodeIdRef = useRef<string>('');
  const liveUpdateTimerRef = useRef<number | null>(null);
  const progressTimerRef = useRef<number | null>(null);

  const isLive = !currentEpisode && currentChannel;

  const PROGRESS_KEY = 'rthk_playback_progress';

  // =========================================================
  // Playback progress
  // =========================================================

  const saveProgress = (
    id: string,
    currentTime: number,
    duration: number
  ) => {
    if (!id || id.startsWith('live-')) return;

    try {
      const data = JSON.parse(
        localStorage.getItem(PROGRESS_KEY) || '{}'
      );

      data[id] = {
        currentTime,
        duration,
        updatedAt: Date.now(),
      };

      localStorage.setItem(
        PROGRESS_KEY,
        JSON.stringify(data)
      );
    } catch {}
  };

  const getSavedProgress = (
    id: string
  ): number | null => {
    if (!id) return null;

    try {
      const data = JSON.parse(
        localStorage.getItem(PROGRESS_KEY) || '{}'
      )[id];

      if (
        data &&
        Date.now() - data.updatedAt <
          7 * 24 * 60 * 60 * 1000
      ) {
        return data.currentTime ?? null;
      }
    } catch {}

    return null;
  };

  const clearProgress = (id: string) => {
    if (!id) return;

    try {
      const data = JSON.parse(
        localStorage.getItem(PROGRESS_KEY) || '{}'
      );

      delete data[id];

      localStorage.setItem(
        PROGRESS_KEY,
        JSON.stringify(data)
      );
    } catch {}
  };

  // =========================================================
  // Live program information
  // =========================================================

  useEffect(() => {
    if (!currentChannel?.id || currentEpisode) {
      if (liveUpdateTimerRef.current) {
        clearInterval(liveUpdateTimerRef.current);
        liveUpdateTimerRef.current = null;
      }

      setLiveProgramInfo(null);
      return;
    }

    const fetchLiveInfo = () => {
      fetchCurrentLiveProgram(currentChannel.id)
        .then(info => {
          if (info) {
            setLiveProgramInfo(info);

            window.dispatchEvent(
              new CustomEvent('live-program-updated', {
                detail: {
                  ...info,
                  channelId: currentChannel.id,
                },
              })
            );
          }
        })
        .catch(() => {});
    };

    fetchLiveInfo();

    liveUpdateTimerRef.current =
      window.setInterval(
        fetchLiveInfo,
        5 * 60 * 1000
      );

    return () => {
      if (liveUpdateTimerRef.current) {
        clearInterval(
          liveUpdateTimerRef.current
        );

        liveUpdateTimerRef.current = null;
      }
    };
  }, [
    currentChannel?.id,
    currentEpisode,
  ]);

  // =========================================================
  // Media Session
  // =========================================================

  const updateMediaSession = useCallback(() => {
    if (!('mediaSession' in navigator)) return;

    const title = isLive
      ? (
          liveProgramInfo?.program ||
          currentChannel?.name ||
          '直播'
        )
      : (
          currentEpisode?.programTitle ||
          currentEpisode?.title ||
          '节目'
        );

    const artist = isLive
      ? (
          liveProgramInfo?.host ||
          currentChannel?.name ||
          '香港电台'
        )
      : (
          currentEpisode?.publishDate || ''
        );

    const album = isLive
      ? (
          currentChannel?.name ||
          '直播'
        )
      : '节目重温';

    try {
      navigator.mediaSession.metadata =
        new MediaMetadata({
          title,
          artist,
          album,
          artwork: [
            {
              src: '/favicon.svg',
              sizes: '512x512',
              type: 'image/svg+xml',
            },
            {
              src: '/favicon.svg',
              sizes: '128x128',
              type: 'image/svg+xml',
            },
            {
              src: '/favicon.svg',
              sizes: '96x96',
              type: 'image/svg+xml',
            },
            {
              src: '/favicon.svg',
              sizes: '64x64',
              type: 'image/svg+xml',
            },
          ],
        });
    } catch (e) {
      console.log(
        'MediaSession error:',
        e
      );
    }
  }, [
    isLive,
    liveProgramInfo,
    currentChannel,
    currentEpisode,
  ]);

  useEffect(() => {
    updateMediaSession();
  }, [updateMediaSession]);

  // =========================================================
  // Polling
  // =========================================================

  const startPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(
        pollingTimerRef.current
      );
    }

    pollingTimerRef.current =
      window.setInterval(() => {
        const audio = audioRef.current;

        if (audio) {
          const time = audio.currentTime;
          const paused = audio.paused;

          if (
            !paused &&
            !isSeekingRef.current
          ) {
            setCurrentTime(time);
          }
        }
      }, 100);

    if (progressTimerRef.current) {
      clearInterval(
        progressTimerRef.current
      );
    }

    progressTimerRef.current =
      window.setInterval(() => {
        const audio = audioRef.current;

        if (
          audio &&
          !audio.paused &&
          currentEpisode?.id
        ) {
          saveProgress(
            currentEpisode.id,
            audio.currentTime,
            audio.duration || 0
          );
        }
      }, 10000);
  }, [currentEpisode?.id]);

  const stopPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(
        pollingTimerRef.current
      );

      pollingTimerRef.current = null;
    }

    if (progressTimerRef.current) {
      clearInterval(
        progressTimerRef.current
      );

      progressTimerRef.current = null;
    }
  }, []);

  // =========================================================
  // Media Session controls
  // =========================================================

  useEffect(() => {
    if (!('mediaSession' in navigator)) {
      return;
    }

    navigator.mediaSession.setActionHandler(
      'play',
      () => {
        const audio = audioRef.current;

        if (audio) {
          audio
            .play()
            .then(() => {
              isPlayingRef.current = true;
              setIsPlaying(true);
              startPolling();
            })
            .catch(() => {});
        }
      }
    );

    navigator.mediaSession.setActionHandler(
      'pause',
      () => {
        const audio = audioRef.current;

        if (audio) {
          audio.pause();

          isPlayingRef.current = false;
          setIsPlaying(false);

          stopPolling();
        }
      }
    );
  }, [
    startPolling,
    stopPolling,
  ]);

  // =========================================================
  // Create audio element
  // =========================================================

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const audio = new Audio();

    audio.crossOrigin = 'anonymous';
    audio.volume = 1;

    audioRef.current = audio;

    const handleTimeUpdate = () => {
      if (audioRef.current) {
        const paused =
          audioRef.current.paused;

        const current =
          audioRef.current.currentTime;

        if (!paused) {
          setCurrentTime(current);
        }

        const dur =
          audioRef.current.duration;

        if (
          dur &&
          isFinite(dur) &&
          dur > 0 &&
          dur !== Infinity
        ) {
          setDuration(dur);
        }
      }
    };

    const handlePlay = () => {
      isPlayingRef.current = true;
      setIsPlaying(true);
      startPolling();
    };

    const handlePause = () => {
      isPlayingRef.current = false;
      setIsPlaying(false);

      stopPolling();

      const audio = audioRef.current;

      if (
        audio &&
        currentEpisode?.id
      ) {
        saveProgress(
          currentEpisode.id,
          audio.currentTime,
          audio.duration || 0
        );
      }

      window.dispatchEvent(
        new Event('playback-stopped')
      );
    };

    const handleEnded = () => {
      isPlayingRef.current = false;
      setIsPlaying(false);

      stopPolling();
      setCurrentTime(0);

      if (currentEpisode?.id) {
        clearProgress(
          currentEpisode.id
        );
      }

      window.dispatchEvent(
        new Event('playback-stopped')
      );
    };

    const handleLoadedMetadata = () => {
      if (
        audio.duration &&
        isFinite(audio.duration) &&
        audio.duration > 0
      ) {
        setDuration(audio.duration);
      }

      setCurrentTime(
        audio.currentTime
      );
    };

    const handleError = () => {
      isPlayingRef.current = false;
      setIsPlaying(false);
    };

    audio.addEventListener(
      'timeupdate',
      handleTimeUpdate
    );

    audio.addEventListener(
      'play',
      handlePlay
    );

    audio.addEventListener(
      'pause',
      handlePause
    );

    audio.addEventListener(
      'ended',
      handleEnded
    );

    audio.addEventListener(
      'loadedmetadata',
      handleLoadedMetadata
    );

    audio.addEventListener(
      'error',
      handleError
    );

    return () => {
      audio.removeEventListener(
        'timeupdate',
        handleTimeUpdate
      );

      audio.removeEventListener(
        'play',
        handlePlay
      );

      audio.removeEventListener(
        'pause',
        handlePause
      );

      audio.removeEventListener(
        'ended',
        handleEnded
      );

      audio.removeEventListener(
        'loadedmetadata',
        handleLoadedMetadata
      );

      audio.removeEventListener(
        'error',
        handleError
      );

      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      if (progressTimerRef.current) {
        clearInterval(
          progressTimerRef.current
        );

        progressTimerRef.current = null;
      }

      audio.pause();
      audio.src = '';
    };
  }, []);

  // =========================================================
  // Play / Pause
  // =========================================================

  const handlePlayPause = useCallback(() => {
    const audio = audioRef.current;

    if (!audio) return;

    if (audio.paused) {
      isPlayingRef.current = true;

      audio
        .play()
        .then(() => {
          setIsPlaying(true);
          startPolling();
        })
        .catch(() => {
          isPlayingRef.current = false;
          setIsPlaying(false);
        });
    } else {
      isPlayingRef.current = false;
      setIsPlaying(false);

      stopPolling();
      audio.pause();

      if (currentEpisode?.id) {
        saveProgress(
          currentEpisode.id,
          audio.currentTime,
          audio.duration || 0
        );
      }

      window.dispatchEvent(
        new Event('playback-stopped')
      );
    }
  }, [
    startPolling,
    stopPolling,
    currentEpisode,
  ]);

  // =========================================================
  // Seek
  // =========================================================

  const handleSeek = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      const audio = audioRef.current;

      if (!audio) return;

      const time = parseFloat(
        e.target.value
      );

      isSeekingRef.current = true;

      audio.currentTime = time;

      setCurrentTime(time);

      setTimeout(() => {
        isSeekingRef.current = false;
      }, 100);
    },
    []
  );

  // =========================================================
  // Format time
  // =========================================================

  const formatTime = useCallback(
    (seconds: number) => {
      if (
        !seconds ||
        isNaN(seconds) ||
        !isFinite(seconds) ||
        seconds === Infinity
      ) {
        return '00:00';
      }

      const h = Math.floor(
        seconds / 3600
      );

      const m = Math.floor(
        (seconds % 3600) / 60
      );

      const s = Math.floor(
        seconds % 60
      );

      if (h > 0) {
        return `${h}:${m
          .toString()
          .padStart(2, '0')}:${s
          .toString()
          .padStart(2, '0')}`;
      }

      return `${m}:${s
        .toString()
        .padStart(2, '0')}`;
    },
    []
  );

  // =========================================================
  // Load media
  // =========================================================

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) return;

    let streamUrl = '';

    if (currentEpisode?.audioUrl) {
      streamUrl =
        currentEpisode.audioUrl;
    } else if (currentEpisode) {
      streamUrl =
        RTHK_LIVE_STREAMS[
          currentEpisode.channelId as keyof typeof RTHK_LIVE_STREAMS
        ] ||
        RTHK_LIVE_STREAMS.radio1;
    } else if (
      currentChannel?.streamUrl
    ) {
      streamUrl =
        currentChannel.streamUrl;
    }

    if (!streamUrl) {
      return;
    }

    const episodeId =
      currentEpisode?.id ||
      `live-${currentChannel?.id || ''}`;

    if (
      lastEpisodeIdRef.current ===
        episodeId &&
      audio.src === streamUrl
    ) {
      return;
    }

    lastEpisodeIdRef.current =
      episodeId;

    fallbackIndexRef.current = 0;

    // Cleanup old HLS
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    audio.pause();

    isPlayingRef.current = false;
    setIsPlaying(false);
    setError(null);

    // =======================================================
    // Initial duration / progress
    // =======================================================

    if (
      !isLive &&
      currentEpisode
    ) {
      const dur =
        currentEpisode.duration ||
        3600;

      setDuration(dur);

      const savedTime =
        getSavedProgress(
          currentEpisode.id
        );

      const start =
        savedTime !== null
          ? savedTime
          : (
              currentEpisode.startTime ||
              0
            );

      try {
        audio.currentTime = start;
      } catch {}
    } else {
      setDuration(0);
      setCurrentTime(0);
    }

    // =======================================================
    // HLS
    // =======================================================

    if (
      streamUrl.includes('.m3u8') &&
      Hls.isSupported()
    ) {
      const hls = new Hls(
        isLive
          ? {
              maxBufferLength: 30,
              maxMaxBufferLength: 60,
            }
          : {
              startPosition:
                currentEpisode?.startTime ||
                0,
            }
      );

      hlsRef.current = hls;

      hls.attachMedia(audio);
      hls.loadSource(streamUrl);

      setError(null);

      hls.on(
        Hls.Events.MANIFEST_PARSED,
        () => {
          setError(null);
          setRetryCount(0);

          let hlsDuration = 0;

          if (
            hls.levels &&
            hls.levels.length > 0
          ) {
            const level =
              hls.levels[0];

            if (level.details) {
              const details =
                level.details as any;

              if (
                typeof details.totalduration ===
                  'number' &&
                details.totalduration > 0
              ) {
                hlsDuration =
                  details.totalduration;
              } else if (
                typeof details.duration ===
                  'number' &&
                details.duration > 0
              ) {
                hlsDuration =
                  details.duration;
              }
            }
          }

          if (hlsDuration > 0) {
            setDuration(hlsDuration);
          }

          if (
            !isLive &&
            currentEpisode
          ) {
            const savedTime =
              getSavedProgress(
                currentEpisode.id
              );

            if (savedTime !== null) {
              audio.currentTime =
                savedTime;
            } else {
              audio.currentTime =
                currentEpisode.startTime ||
                0;
            }
          }

          audio
            .play()
            .then(() => {
              setIsPlaying(true);
              startPolling();
            })
            .catch(() => {});
        }
      );

      hls.on(
        Hls.Events.ERROR,
        (_e: any, data: any) => {
          if (!data.fatal) return;

          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              if (isLive) {
                const channelId =
                  currentChannel?.id ||
                  'radio2';

                const fallbackLiveUrls =
                  getLiveStreamFallbackUrls(
                    channelId
                  );

                const currentUrl =
                  hls.url ||
                  streamUrl;

                const currentIndex =
                  fallbackLiveUrls.indexOf(
                    currentUrl
                  );

                if (
                  currentIndex >= 0 &&
                  currentIndex <
                    fallbackLiveUrls.length - 1
                ) {
                  const nextUrl =
                    fallbackLiveUrls[
                      currentIndex + 1
                    ];

                  setRetryCount(0);

                  setError(
                    '主线路连接失败，正在切换备用线路...'
                  );

                  setTimeout(() => {
                    hls.loadSource(
                      nextUrl
                    );

                    hls.startLoad();
                  }, 500);
                } else {
                  const maxRetries = 2;

                  if (
                    retryCount <
                    maxRetries
                  ) {
                    setRetryCount(
                      prev =>
                        prev + 1
                    );

                    setError(
                      `连接中断，正在重试 (${retryCount + 1}/${maxRetries})...`
                    );

                    setTimeout(
                      () => {
                        hls.loadSource(
                          fallbackLiveUrls[0]
                        );

                        hls.startLoad();
                      },
                      1000 *
                        (retryCount + 1)
                    );
                  } else {
                    setError(
                      '直播连接失败，请检查网络后重试'
                    );

                    hls.destroy();
                  }
                }
              } else if (
                currentEpisode?.audioUrl &&
                currentEpisode.channelId &&
                currentEpisode.programId &&
                currentEpisode.publishDate
              ) {
                const fallbackUrls =
                  getFallbackUrls(
                    currentEpisode.channelId,
                    currentEpisode.programId,
                    currentEpisode.publishDate
                  );

                const currentUrl =
                  hls.url ||
                  streamUrl;

                const currentIndex =
                  fallbackUrls.indexOf(
                    currentUrl
                  );

                if (
                  currentIndex >= 0 &&
                  currentIndex <
                    fallbackUrls.length - 1
                ) {
                  fallbackIndexRef.current =
                    currentIndex + 1;
                } else {
                  fallbackIndexRef.current = 0;
                }

                if (
                  fallbackIndexRef.current <
                  fallbackUrls.length
                ) {
                  const nextUrl =
                    fallbackUrls[
                      fallbackIndexRef.current
                    ];

                  if (
                    nextUrl !== streamUrl
                  ) {
                    hls.loadSource(
                      nextUrl
                    );

                    hls.startLoad();
                  }
                }
              } else {
                hls.startLoad();
              }

              break;

            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;

            default:
              hls.destroy();
              break;
          }
        }
      );
    } else {
      // =====================================================
      // Native audio
      // =====================================================

      audio.src = streamUrl;
      audio.load();

      audio.oncanplay = () => {
        if (
          !isLive &&
          currentEpisode
        ) {
          const savedTime =
            getSavedProgress(
              currentEpisode.id
            );

          if (savedTime !== null) {
            audio.currentTime =
              savedTime;
          } else {
            audio.currentTime =
              currentEpisode.startTime ||
              0;
          }
        }

        const dur =
          audio.duration;

        if (
          dur &&
          isFinite(dur) &&
          dur > 0 &&
          dur !== Infinity
        ) {
          setDuration(dur);
        }

        audio
          .play()
          .then(() => {
            setIsPlaying(true);
            startPolling();
          })
          .catch(() => {});
      };
    }
  }, [
    currentEpisode,
    currentChannel,
    retryCount,
  ]);

  // =========================================================
  // Clear player
  // =========================================================

  useEffect(() => {
    if (
      !currentChannel &&
      !currentEpisode
    ) {
      isPlayingRef.current = false;

      setIsPlaying(false);
      setDuration(0);
      setCurrentTime(0);
      setError(null);
    }
  }, [
    currentChannel,
    currentEpisode,
  ]);

  // =========================================================
  // Save playback progress
  // =========================================================

  useEffect(() => {
    if (
      !currentEpisode ||
      isLive ||
      !isPlaying
    ) {
      if (
        progressTimerRef.current
      ) {
        clearInterval(
          progressTimerRef.current
        );

        progressTimerRef.current = null;
      }

      return;
    }

    progressTimerRef.current =
      window.setInterval(() => {
        const audio =
          audioRef.current;

        if (
          audio &&
          currentEpisode.id
        ) {
          saveProgress(
            currentEpisode.id,
            audio.currentTime,
            audio.duration || 0
          );
        }
      }, 10 * 1000);

    return () => {
      if (
        progressTimerRef.current
      ) {
        clearInterval(
          progressTimerRef.current
        );

        progressTimerRef.current = null;
      }

      const audio =
        audioRef.current;

      if (
        audio &&
        currentEpisode?.id
      ) {
        saveProgress(
          currentEpisode.id,
          audio.currentTime,
          audio.duration || 0
        );
      }
    };
  }, [
    currentEpisode?.id,
    isLive,
    isPlaying,
  ]);

  // =========================================================
  // Trigger autoplay
  // =========================================================

  useEffect(() => {
    const handleTriggerAutoplay =
      () => {
        const audio =
          audioRef.current;

        if (
          audio &&
          (
            audio.paused ||
            audio.currentTime === 0
          )
        ) {
          audio
            .play()
            .then(() => {
              isPlayingRef.current =
                true;

              setIsPlaying(true);
              startPolling();
            })
            .catch(() => {});
        }
      };

    window.addEventListener(
      'trigger-autoplay',
      handleTriggerAutoplay
    );

    return () => {
      window.removeEventListener(
        'trigger-autoplay',
        handleTriggerAutoplay
      );
    };
  }, [startPolling]);

  // =========================================================
  // No player
  // =========================================================

  if (
    !currentChannel &&
    !currentEpisode
  ) {
    return null;
  }

  // =========================================================
  // DARK PLAYER UI
  // =========================================================

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '70px',
        left: 0,
        right: 0,

        background: isExpanded
          ? '#111827'
          : 'transparent',

        borderTop: isExpanded
          ? '1px solid #374151'
          : 'none',

        boxShadow: isExpanded
          ? '0 -4px 20px rgba(0, 0, 0, 0.35)'
          : 'none',

        zIndex: 45,

        height: isExpanded
          ? isLive
            ? '70px'
            : '100px'
          : '5px',

        color: '#f9fafb',

        transition:
          'background 0.2s ease, box-shadow 0.2s ease',
      }}
    >
      {/* =====================================================
          Collapsed
          ===================================================== */}

      {!isExpanded && (
        <div
          style={{
            position: 'relative',
            height: '5px',
          }}
        >
          <div
            style={{
              height: '5px',
              background: '#d40000',
              width: '100%',
            }}
          />

          <div
            onClick={() =>
              setIsExpanded(true)
            }
            style={{
              position: 'absolute',
              right: '10px',
              top: '-22px',

              width: '40px',
              height: '25px',

              background:
                'rgba(212, 0, 0, 0.85)',

              borderRadius:
                '8px 8px 0 0',

              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',

              cursor: 'pointer',
              zIndex: 51,

              boxShadow:
                '0 -2px 8px rgba(0,0,0,0.25)',
            }}
          >
            <span
              style={{
                color: '#ffffff',
                fontSize: '10px',
              }}
            >
              ▲
            </span>
          </div>
        </div>
      )}

      {/* =====================================================
          Expanded
          ===================================================== */}

      {isExpanded && (
        <>
          {/* Collapse button */}
          <div
            onClick={() =>
              setIsExpanded(false)
            }
            style={{
              position: 'absolute',
              top: '-20px',
              right: '10px',

              width: '60px',
              height: '25px',

              background:
                'rgba(212, 0, 0, 0.85)',

              borderRadius:
                '15px 15px 0 0',

              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',

              cursor: 'pointer',
              zIndex: 51,

              boxShadow:
                '0 -2px 8px rgba(0,0,0,0.25)',
            }}
          >
            <span
              style={{
                color: '#ffffff',
                fontSize: '12px',
              }}
            >
              ▼
            </span>
          </div>

          {/* =================================================
              Program information
              ================================================= */}

          <div
            style={{
              position: 'absolute',
              left: '10px',
              top: isLive
                ? '8px'
                : '4px',

              width:
                'calc(100% - 60px)',

              minWidth: 0,
            }}
          >
            {/* Live indicator */}

            {isLive && (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  marginBottom: '2px',
                }}
              >
                <span
                  style={{
                    background: '#d40000',
                    color: '#ffffff',

                    fontSize: '9px',
                    fontWeight: 'bold',

                    padding:
                      '2px 6px',

                    borderRadius: '3px',

                    lineHeight: '1.2',
                  }}
                >
                  LIVE
                </span>

                <span
                  style={{
                    fontSize: '9px',
                    color: '#ef4444',
                    fontWeight: 'bold',
                  }}
                >
                  现正播放
                </span>
              </div>
            )}

            {/* Program title */}

            <div
              style={{
                fontSize: isLive
                  ? '13px'
                  : '14px',

                fontWeight: 'bold',

                color: '#f9fafb',

                overflow: 'hidden',
                textOverflow:
                  'ellipsis',
                whiteSpace:
                  'nowrap',

                lineHeight: '1.4',
              }}
            >
              {currentEpisode
                ? (
                    currentEpisode.programTitle ||
                    currentEpisode.title
                  )
                : isLive
                  ? `${currentChannel?.name || ''} - ${
                      liveProgramInfo?.program ||
                      '未知'
                    }`
                  : (
                      liveProgramInfo?.program ||
                      currentChannel?.name ||
                      '未知'
                    )}
            </div>

            {/* Second line */}

            <div
              style={{
                fontSize: '11px',

                color: '#9ca3af',

                overflow: 'hidden',
                textOverflow:
                  'ellipsis',
                whiteSpace:
                  'nowrap',

                lineHeight: '1.4',
              }}
            >
              {error ? (
                <span
                  style={{
                    color: '#f87171',
                  }}
                >
                  {error}
                </span>
              ) : isLive ? (
                <span>
                  {liveProgramInfo?.host && (
                    <span
                      style={{
                        marginRight:
                          '8px',
                      }}
                    >
                      主持:{' '}
                      {
                        liveProgramInfo.host
                      }
                    </span>
                  )}

                  {liveProgramInfo?.time && (
                    <span
                      style={{
                        color: '#6b7280',
                      }}
                    >
                      {
                        liveProgramInfo.time
                      }
                    </span>
                  )}
                </span>
              ) : currentEpisode ? (
                <span>
                  {currentEpisode.programTitle && (
                    <span>
                      {
                        currentEpisode.programTitle
                      }{' '}
                      -{' '}
                    </span>
                  )}

                  {
                    currentEpisode.publishDate ||
                    currentEpisode.description ||
                    ''
                  }
                </span>
              ) : (
                currentChannel?.description ||
                ''
              )}
            </div>
          </div>

          {/* =================================================
              Play button
              ================================================= */}

          <div
            style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform:
                'translateY(-50%)',
            }}
          >
            <button
              onClick={
                handlePlayPause
              }
              aria-label={
                isPlaying
                  ? '暂停'
                  : '播放'
              }
              style={{
                width: '34px',
                height: '34px',

                borderRadius:
                  '50%',

                background:
                  'rgba(255,255,255,0.04)',

                border:
                  '1px solid #374151',

                cursor: 'pointer',

                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',

                transition:
                  'all 0.2s ease',
              }}
            >
              {isPlaying ? (
                <span
                  style={{
                    color: '#f9fafb',
                    fontSize: '18px',
                    letterSpacing:
                      '2px',
                  }}
                >
                  ❚❚
                </span>
              ) : (
                <span
                  style={{
                    color: '#ef4444',
                    fontSize: '19px',
                    marginLeft:
                      '2px',
                  }}
                >
                  ▶
                </span>
              )}
            </button>
          </div>

          {/* =================================================
              Timeline
              ================================================= */}

          {!isLive && (
            <div
              style={{
                position: 'absolute',

                bottom: '6px',
                left: '10px',
                right: '10px',

                display: 'flex',
                alignItems: 'center',

                gap: '8px',
              }}
            >
              <span
                style={{
                  fontSize: '11px',
                  color: '#9ca3af',
                  minWidth: '40px',
                }}
              >
                {formatTime(
                  currentTime
                )}
              </span>

              <input
                type="range"
                min={0}
                max={Math.max(
                  duration,
                  1
                )}
                value={Math.min(
                  currentTime,
                  Math.max(
                    duration,
                    1
                  )
                )}
                onChange={
                  handleSeek
                }
                style={{
                  flex: 1,
                  height: '4px',
                  cursor: 'pointer',

                  accentColor:
                    '#d40000',

                  background:
                    '#374151',
                }}
              />

              <span
                style={{
                  fontSize: '11px',

                  color: '#9ca3af',

                  minWidth: '45px',

                  textAlign:
                    'right',
                }}
              >
                {formatTime(
                  duration
                )}
              </span>
            </div>
          )}
        </>
      )}

      {/* Hidden audio element */}

      <audio
        ref={audioRef}
        style={{
          display: 'none',
        }}
      />
    </div>
  );
}
